import { useCallback, useMemo } from 'react'
import { useLazyGetEntitySchemaQuery } from '../../../store/integrations/integrations'
import { useExecuteAiAgentMutation } from '../../../store/ai/aiAgent'
import { type AccountOpeningCollection, type AccountOpeningCollectionStep, type AccountOpeningStep } from '../../../models/AccountOpeningDescriptor'
import {
  collectionSchemaSource,
  itemNeedsSchema,
  itemSchemaKey,
  itemsOf,
  prefillSourcesFor,
  prefillWanted,
  type CollectionContext,
  type ResolvedItem,
} from '../domain/collections'
import { contactFacts } from '../domain/contactFallback'
import { fetchPrefilled } from '../domain/prefillSources'
import { type ContactOption, type WizardContext } from '../domain/types'
import { type DraftAction, type FetchedSchema, type WizardDraft } from '../domain/wizardDraft'

export interface PreparedSchemas {
  items: ResolvedItem[]
  schemas: Map<string, Record<string, unknown>>
}

export interface CollectionSchemas {
  hydrateCollection: (collection: AccountOpeningCollection) => Promise<PreparedSchemas>
  prefillCollection: (collection: AccountOpeningCollection, prepared: PreparedSchemas) => Promise<void>
  fillValues: (collection: AccountOpeningCollection, filled: Map<string, Record<string, unknown>>) => void
}

export const useCollectionSchemas = ({
  ctx,
  collectionCtx,
  steps,
  readOnly,
  contactOptions,
  apply,
}: {
  ctx: WizardContext
  collectionCtx: CollectionContext
  steps: AccountOpeningStep[]
  readOnly: boolean
  contactOptions: ContactOption[]
  apply: (action: DraftAction) => WizardDraft
}): CollectionSchemas => {
  const [fetchEntitySchema] = useLazyGetEntitySchemaQuery()
  const [executePrefill] = useExecuteAiAgentMutation()

  const contactFactsById = useMemo(() => new Map(contactOptions.map((option) => [option.id, contactFacts(option)])), [contactOptions])

  const hydrateSchemas = useCallback(
    async (collection: AccountOpeningCollection, items: ResolvedItem[]): Promise<Map<string, FetchedSchema>> => {
      const source = collectionSchemaSource(steps, collection)
      const fetched = new Map<string, FetchedSchema>()
      if (!source) {
        return fetched
      }
      await Promise.all(
        items.map(async (item) => {
          if (!itemNeedsSchema(item, source.valueFrom)) {
            return
          }
          const response = await fetchEntitySchema({
            accountId: ctx.accountId,
            connector: ctx.connector,
            entity: source.entity,
            keyedBy: source.keyedBy,
            key: itemSchemaKey(item, source.valueFrom),
          }).unwrap()
          fetched.set(item.ref, { schema: response.data ?? {}, fetchedAt: new Date().toISOString() })
        }),
      )
      return fetched
    },
    [steps, ctx.accountId, ctx.connector, fetchEntitySchema],
  )

  const prefillStepFor = useCallback(
    (collection: AccountOpeningCollection) =>
      steps.find((step): step is AccountOpeningCollectionStep => step.kind === 'collection' && step.collection === collection && !!step.prefill),
    [steps],
  )

  const runPrefills = useCallback(
    async (collection: AccountOpeningCollection, items: ResolvedItem[], schemas: Map<string, Record<string, unknown>>) => {
      const prefillStep = prefillStepFor(collection)
      const prefill = prefillStep?.prefill
      if (!prefillStep || !prefill || readOnly) {
        return new Map<string, Record<string, unknown>>()
      }
      const withFetchedSchema = (item: ResolvedItem): ResolvedItem => {
        const schema = schemas.get(item.ref)
        if (!schema) {
          return item
        }
        return item.collection === 'accounts'
          ? { ...item, account: { ...item.account, schemaSnapshot: schema } }
          : { ...item, party: { ...item.party, kycSchemaSnapshot: schema } }
      }
      const targets = items
        .map(withFetchedSchema)
        .filter((item) => prefillWanted(prefillStep, item))
        .map((item) => ({ key: item.ref, sources: prefillSourcesFor(item, ctx, prefillStep) }))

      const { filled } = await fetchPrefilled({ executePrefill, accountId: ctx.accountId, prefill, targets })
      return filled
    },
    [prefillStepFor, readOnly, ctx, executePrefill],
  )

  const hydrateCollection = useCallback(
    async (collection: AccountOpeningCollection): Promise<PreparedSchemas> => {
      const items = itemsOf(collection, collectionCtx)
      const fetched = await hydrateSchemas(collection, items)
      if (fetched.size > 0) {
        apply({ type: 'schemasFetched', collection, schemas: fetched })
      }
      return { items, schemas: new Map([...fetched].map(([ref, entry]) => [ref, entry.schema])) }
    },
    [collectionCtx, hydrateSchemas, apply],
  )

  const fillValues = useCallback(
    (collection: AccountOpeningCollection, filled: Map<string, Record<string, unknown>>) => {
      if (readOnly) {
        return
      }
      apply({ type: 'valuesFilled', collection, values: filled, contactFacts: contactFactsById })
    },
    [apply, readOnly, contactFactsById],
  )

  const prefillCollection = useCallback(
    async (collection: AccountOpeningCollection, prepared: PreparedSchemas) => {
      const filled = await runPrefills(collection, prepared.items, prepared.schemas)
      if (filled.size === 0 && collection === 'accounts') {
        return
      }
      fillValues(collection, filled)
    },
    [runPrefills, fillValues],
  )

  return { hydrateCollection, prefillCollection, fillValues }
}
